/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CELL_NO_OVERFLOW_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  defaultCurrencyCellRendererDecorator,
  dictionaryValueFormatter,
  outerLabelCellRendererDecorator,
} from '@gh/common-internal-shared';

import { DateTimeTypes, getRouteInitData, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  typedValueFormatter,
} from '@gh/core-ui-grid';

import { find } from 'lodash';

import {
  calculateInvoiceTotals,
  invoiceNumberSorting,
  JobDrawsDetailsInvoice,
  JobDrawsInvoicePlacement,
} from '../../../resources/job-details-tab';
import { DrawInvoiceStatus } from '@gh/builder-shared-data';
import { JobFinancialsDrawsFilterCode } from '../../../resources/job-financials.model';

@Component({
  selector: 'gh-job-details-draws-history-subtab',
  template: require('./job-details-draws-history-subtab.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class JobDetailsDrawsHistorySubtabComponent implements OnInit, OnChanges {
  @Input() invoices: JobDrawsDetailsInvoice[];
  @Input() invoiceTotals: JobDrawsDetailsInvoice[];
  @Input() selectedInvoiceId: ID;
  @Input() filter: JobFinancialsDrawsFilterCode = JobFinancialsDrawsFilterCode.isOnlyActiveDraws;

  @Output() invoiceSelected = new EventEmitter<JobDrawsDetailsInvoice>();
  @Output() openDraw = new EventEmitter<JobDrawsDetailsInvoice>();
  @Output() openInXero = new EventEmitter<JobDrawsDetailsInvoice>();

  invoiceGridDefinition: GridDefinition;
  paymentGridDefinition: GridDefinition;

  selectedInvoice?: JobDrawsDetailsInvoice;
  showActive: boolean = true;
  filteredInvoices: JobDrawsDetailsInvoice[];

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.invoiceGridDefinition = this.buildInvoiceGridDefinition();
    this.paymentGridDefinition = this.buildPaymentGridDefinition();
    this.filteredInvoices = this.getActiveInvoices();
    this.invoiceTotals = calculateInvoiceTotals(this.getActiveInvoices());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('selectedInvoiceId')) {
      this.selectedInvoice = find<JobDrawsDetailsInvoice>(this.invoices, { invoiceId: this.selectedInvoiceId });
    }

    if (changes.hasOwnProperty('invoices')) {
      this.filteredInvoices = this.getActiveInvoices();
      this.invoiceTotals = calculateInvoiceTotals(this.getActiveInvoices());
    }
  }

  getActiveInvoices(): JobDrawsDetailsInvoice[] {
    return this.invoices.filter((el) =>
      el.status === DrawInvoiceStatus.Authorized || el.status === DrawInvoiceStatus.Paid);
  }

  onToggleShowInvoices(): void {
    this.showActive = !this.showActive;
    this.filteredInvoices = !this.showActive ? this.invoices : this.getActiveInvoices();
    this.invoiceTotals = calculateInvoiceTotals(this.getActiveInvoices());
  }

  private buildInvoiceGridDefinition(): GridDefinition {
    const { invoiceGridLayout, invoiceTypeDict } = getRouteInitData(this.route);

    const pinnedPriceRenderer = defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice));
    const gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        invoiceNumber: {
          comparator: invoiceNumberSorting,
        },
        accountingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        type: { valueFormatter: dictionaryValueFormatter(invoiceTypeDict) },
        amountWithGst: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          cellClass: [CELL_NO_OVERFLOW_CLASS, CELL_TEXT_RIGHT_CLASS],
          pinnedRowCellRenderer: outerLabelCellRendererDecorator(
            ({ data }) => this.getTotalLabel(data),
            20)(pinnedPriceRenderer),
        },
        balance: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          pinnedRowCellRenderer: pinnedPriceRenderer,
        },
        status: {
          cellClass: ({ data }: { data: JobDrawsDetailsInvoice }) => ({
            [DrawInvoiceStatus.Draft]: 'cell--unimportant',
            [DrawInvoiceStatus.Submitted]: 'cell--unimportant',
            [DrawInvoiceStatus.Authorized]: 'cell--positive',
            [DrawInvoiceStatus.Paid]: 'cell--positive',
            [DrawInvoiceStatus.Deleted]: 'cell--negative',
            [DrawInvoiceStatus.Voided]: 'cell--negative',
          }[data.status]),
        },
      },
      {
        rowSelection: 'single',
      });
    gridDefinition.bind(invoiceGridLayout);
    return gridDefinition;
  }

  private buildPaymentGridDefinition(): GridDefinition {
    const { paymentGridLayout } = getRouteInitData(this.route);

    const gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      paidDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      amount: buildNumberColumn(NumberTypes.SignedPrice),
    });
    gridDefinition.bind(paymentGridLayout);
    return gridDefinition;
  }

  private getTotalLabel(invoice: JobDrawsDetailsInvoice): string {
    switch (invoice.placement) {
      case JobDrawsInvoicePlacement.TotalContract:
        return this.translations.get('lbl_contract');
      case JobDrawsInvoicePlacement.TotalChangeOrders:
        return this.translations.get('lbl_variations');
      case JobDrawsInvoicePlacement.TotalDraws:
        return this.translations.get('lbl_total_draws');
      case JobDrawsInvoicePlacement.TotalCrNotes:
        return this.translations.get('lbl_cr_notes');
      default:
        return '';
    }
  }
}
